import { matchPath } from 'react-router-dom';
import {
  hasModuleReviewAccess,
  isReviewScopedModuleId,
  resolveReviewAccessDeniedMessage,
} from '../auth/reviewGroupAccess';
import {
  YSTNJ_COGNITO_GROUP,
  YSTNJ_RUNTIME_PERSONA,
  parseRuntimePersonaProjection,
} from '../auth/ystnjCognitoGroupVocabulary';
import { parseCanonicalOrMigrationCognitoGroup } from '../auth/ystnjCognitoGroupVocabularyCompat';
import {
  resolveEntryPath,
  resolveModuleFromPath,
  SETTINGS_MODULE,
  SHELL_MODULES,
  type ModuleId,
  type ShellModule,
  type ShellPersona,
} from '../app-shell/moduleRegistry';
import {
  CANONICAL_PUBLIC_ROUTES,
  LEGACY_SHELL_ALIAS_REWRITES,
  PARENT_PORTAL_ALIAS_PREFIX as GENERATED_PARENT_PORTAL_ALIAS_PREFIX,
  PARENT_PORTAL_CANONICAL_PREFIX as GENERATED_PARENT_PORTAL_CANONICAL_PREFIX,
  PORTAL_FAMILY_ALIAS_REWRITES,
  POST_AUTH_PUBLIC_ROUTES,
  PUBLIC_FORBIDDEN_ALIASES,
  PUBLIC_ROUTE_REWRITE_MAP,
  RUNTIME_CANONICAL_SHELL_PREFIXES,
  SETTINGS_FORBIDDEN_ALIASES,
  type PrefixRewriteRule,
} from './generated/canonicalRoutes';

const MODULE_IDS: ModuleId[] = ['assistant', 'run-reports', 'admissions', 'attendance', 'financial-aid', 'payroll', 'students-families', 'faculty', 'settings', 'tuition'];
const STAFF_PERSONAS: ShellPersona[] = ['staff', 'administrator', 'super-administrator'];
const SHELL_REVIEW_GATED_MODULE_IDS = new Set<ModuleId>(['admissions']);

const normalizePersonaToken = (persona: string): string =>
  persona.trim().toLowerCase();

const canonicalizePersona = (persona: string): ShellPersona => {
  const runtimePersona = parseRuntimePersonaProjection(persona);
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.PARENT) {
    return YSTNJ_RUNTIME_PERSONA.PARENT;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.STAFF) {
    return YSTNJ_RUNTIME_PERSONA.STAFF;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR;
  }
  if (runtimePersona === YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }

  const canonicalGroup = parseCanonicalOrMigrationCognitoGroup(normalizePersonaToken(persona));
  if (canonicalGroup === YSTNJ_COGNITO_GROUP.PARENT) {
    return YSTNJ_RUNTIME_PERSONA.PARENT;
  }
  if (
    canonicalGroup === YSTNJ_COGNITO_GROUP.STAFF
    || canonicalGroup === YSTNJ_COGNITO_GROUP.EDITOR
    || canonicalGroup === YSTNJ_COGNITO_GROUP.NURSE
  ) {
    return YSTNJ_RUNTIME_PERSONA.STAFF;
  }
  if (
    canonicalGroup === YSTNJ_COGNITO_GROUP.ADMIN
    || canonicalGroup === YSTNJ_COGNITO_GROUP.OWNER
  ) {
    return YSTNJ_RUNTIME_PERSONA.ADMINISTRATOR;
  }
  if (canonicalGroup === YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR) {
    return YSTNJ_RUNTIME_PERSONA.SUPER_ADMINISTRATOR;
  }
  return YSTNJ_RUNTIME_PERSONA.GUEST;
};

export const PARENT_PORTAL_CANONICAL_PREFIX = GENERATED_PARENT_PORTAL_CANONICAL_PREFIX;
export const PARENT_PORTAL_ALIAS_PREFIX = GENERATED_PARENT_PORTAL_ALIAS_PREFIX;

const CANONICAL_SHELL_PREFIXES = [...RUNTIME_CANONICAL_SHELL_PREFIXES];
const TEMP_SHELL_ALIAS_REWRITES: PrefixRewriteRule[] = [
  {
    fromPrefix: '/parent/portal/temp/donations',
    toPrefix: '/parent/portal/donations',
    defaultPath: '/parent/portal/donations',
  },
  {
    fromPrefix: '/parent/portal/temp/tuition',
    toPrefix: '/parent/portal/tuition',
    defaultPath: '/parent/portal/tuition',
  },
  {
    fromPrefix: '/school/temp/tuition/administrator',
    toPrefix: '/administrator/tuition',
    defaultPath: '/administrator/tuition',
  },
  {
    fromPrefix: '/school/temp/tuition',
    toPrefix: '/school/tuition',
    defaultPath: '/school/tuition',
  },
];

const CANONICAL_PUBLIC_PATHS = new Set(CANONICAL_PUBLIC_ROUTES);
const SETTINGS_FORBIDDEN_PATHS = new Set(SETTINGS_FORBIDDEN_ALIASES);
const PUBLIC_FORBIDDEN_PATHS = new Set(PUBLIC_FORBIDDEN_ALIASES);
const POST_AUTH_PUBLIC_PREFIXES = [...POST_AUTH_PUBLIC_ROUTES];
const INTAKE_PARENT_DEFAULT_ROUTE = '/parent/portal/dashboard';
const SELF_GUARDED_SCHOOL_ROUTE_PREFIXES = ['/school/payroll'] as const;
const ATTENDANCE_ALLOWED_GROUPS = new Set([
  YSTNJ_COGNITO_GROUP.STAFF,
  YSTNJ_COGNITO_GROUP.ADMIN,
  YSTNJ_COGNITO_GROUP.OWNER,
  YSTNJ_COGNITO_GROUP.SUPER_ADMINISTRATOR,
  'administrator',
]);
const DISCONTINUED_SETTINGS_ROUTE_PREFIXES = [
  '/school/settings/integrations',
  '/school/settings/integrations/assistant-workflows',
  '/school/settings/integrations/ui-consultant',
] as const;

const normalizePath = (pathname: string): string =>
  pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '') || '/';

const isPathAtOrUnderPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const applyPrefixRewrite = (
  pathname: string,
  rewrite: PrefixRewriteRule
): string | null => {
  if (pathname === rewrite.fromPrefix) {
    return rewrite.defaultPath ?? rewrite.toPrefix;
  }
  if (pathname.startsWith(`${rewrite.fromPrefix}/`)) {
    return pathname.replace(rewrite.fromPrefix, rewrite.toPrefix);
  }
  return null;
};

export const canonicalizeParentPortalDefaultPath = (pathname: string): string => {
  if (pathname === PARENT_PORTAL_CANONICAL_PREFIX) {
    return `${PARENT_PORTAL_CANONICAL_PREFIX}/dashboard`;
  }
  if (pathname === PARENT_PORTAL_ALIAS_PREFIX) {
    return `${PARENT_PORTAL_ALIAS_PREFIX}/applications`;
  }
  return pathname;
};

export const canonicalizeLegacyPublicPath = (pathname: string): string => {
  const rewritten = PUBLIC_ROUTE_REWRITE_MAP[pathname];
  if (rewritten) return rewritten;
  return pathname;
};

export const canonicalizeLegacyShellAliasPath = (pathname: string): string => {
  for (const rewrite of TEMP_SHELL_ALIAS_REWRITES) {
    const candidate = applyPrefixRewrite(pathname, rewrite);
    if (candidate) return candidate;
  }
  for (const rewrite of LEGACY_SHELL_ALIAS_REWRITES) {
    const candidate = applyPrefixRewrite(pathname, rewrite);
    if (candidate) return candidate;
  }
  for (const rewrite of PORTAL_FAMILY_ALIAS_REWRITES) {
    const candidate = applyPrefixRewrite(pathname, rewrite);
    if (candidate) return candidate;
  }
  return pathname;
};

const LEGACY_ADMISSIONS_TABS = new Set(['children', 'family', 'documents', 'notes', 'tuition']);
const EXTERNAL_ADMISSIONS_TABS = new Set(['children', 'family', 'documents', 'notes', 'tuition']);
const EXTERNAL_FINANCIAL_AID_TABS = new Set([
  'family-profile',
  'income-calculation',
  'assets-equity',
  'expenses-spending',
  'contract-flags',
  'corrections',
  'documents',
  'notes',
]);

export const canonicalizeExternalApplicantPath = (pathname: string): string => {
  const detailWithTab = matchPath(
    { path: '/applicant/:applicationId/:tab', end: true },
    pathname,
  );
  if (detailWithTab?.params.applicationId && detailWithTab.params.tab) {
    const tab = detailWithTab.params.tab.toLowerCase();
    if (EXTERNAL_ADMISSIONS_TABS.has(tab)) {
      return `/school/admissions/${tab}/${detailWithTab.params.applicationId}`;
    }
    if (EXTERNAL_FINANCIAL_AID_TABS.has(tab)) {
      return `/school/financialaid/staff/${detailWithTab.params.applicationId}/${tab}`;
    }
  }

  const detailNoTab = matchPath(
    { path: '/applicant/:applicationId', end: true },
    pathname,
  );
  if (detailNoTab?.params.applicationId) {
    return `/school/admissions/children/${detailNoTab.params.applicationId}`;
  }

  return pathname;
};

export const canonicalizeLegacyAdmissionsPath = (pathname: string): string => {
  const nestedTabMatch = matchPath(
    { path: '/school/admissions/:tab/:familyId/:nextTab', end: true },
    pathname,
  );
  if (
    nestedTabMatch?.params.tab
    && nestedTabMatch.params.familyId
    && nestedTabMatch.params.nextTab
  ) {
    const currentTab = nestedTabMatch.params.tab.toLowerCase();
    const nextTab = nestedTabMatch.params.nextTab.toLowerCase();
    if (LEGACY_ADMISSIONS_TABS.has(currentTab) && LEGACY_ADMISSIONS_TABS.has(nextTab)) {
      return `/school/admissions/${nextTab}/${nestedTabMatch.params.familyId}`;
    }
  }

  const canonicalTabMatch = matchPath(
    { path: '/school/admissions/:tab/:familyId', end: true },
    pathname,
  );
  if (canonicalTabMatch?.params.tab && canonicalTabMatch.params.familyId) {
    const tab = canonicalTabMatch.params.tab.toLowerCase();
    if (LEGACY_ADMISSIONS_TABS.has(tab)) {
      return `/school/admissions/${tab}/${canonicalTabMatch.params.familyId}`;
    }
  }

  const detailMatch = matchPath(
    { path: '/school/admissions/applicant/:familyId/:tab', end: true },
    pathname,
  );
  if (detailMatch?.params.familyId && detailMatch.params.tab) {
    const tab = detailMatch.params.tab.toLowerCase();
    if (LEGACY_ADMISSIONS_TABS.has(tab)) {
      return `/school/admissions/${tab}/${detailMatch.params.familyId}`;
    }
  }

  const applicantMatch = matchPath(
    { path: '/school/admissions/applicant/:familyId', end: true },
    pathname,
  );
  if (applicantMatch?.params.familyId) {
    return `/school/admissions/children/${applicantMatch.params.familyId}`;
  }

  return pathname;
};

export const normalizeShellPath = (pathname: string): string =>
  canonicalizeExternalApplicantPath(
    canonicalizeLegacyAdmissionsPath(
      canonicalizeLegacyPublicPath(
        canonicalizeParentPortalDefaultPath(
          canonicalizeLegacyShellAliasPath(normalizePath(pathname))
        )
      )
    )
  );

export const toPathname = (path: string): string => {
  try {
    return new URL(path, 'https://triedge.local').pathname;
  } catch {
    const [pathname] = path.split('?');
    return pathname || path;
  }
};

export const isParentRoutePath = (pathname: string): boolean =>
  (() => {
    const normalized = normalizeShellPath(pathname);
    return (
      isPathAtOrUnderPrefix(normalized, PARENT_PORTAL_CANONICAL_PREFIX)
      || isPathAtOrUnderPrefix(normalized, PARENT_PORTAL_ALIAS_PREFIX)
    );
  })();

export const isSchoolRoutePath = (pathname: string): boolean =>
  normalizeShellPath(pathname).startsWith('/school/');

export const isAdministratorRoutePath = (pathname: string): boolean =>
  normalizeShellPath(pathname).startsWith('/administrator/');

export const isSuperAdministratorRoutePath = (pathname: string): boolean =>
  normalizeShellPath(pathname).startsWith('/super-administrator/');

const isDiscontinuedSettingsPath = (pathname: string): boolean => {
  const normalized = normalizeShellPath(pathname);
  return DISCONTINUED_SETTINGS_ROUTE_PREFIXES.some((prefix) =>
    isPathAtOrUnderPrefix(normalized, prefix)
  );
};

export const isSelfGuardedSchoolRoutePath = (pathname: string): boolean => {
  const normalized = normalizeShellPath(toPathname(pathname));
  return SELF_GUARDED_SCHOOL_ROUTE_PREFIXES.some((prefix) => isPathAtOrUnderPrefix(normalized, prefix));
};

export const isCanonicalRoutePath = (pathname: string): boolean => {
  const normalized = normalizeShellPath(pathname);
  if (SETTINGS_FORBIDDEN_PATHS.has(normalized) || PUBLIC_FORBIDDEN_PATHS.has(normalized)) {
    return false;
  }
  if (CANONICAL_PUBLIC_PATHS.has(normalized)) {
    return true;
  }
  return CANONICAL_SHELL_PREFIXES.some((prefix) => isPathAtOrUnderPrefix(normalized, prefix));
};

export const isModuleId = (value: string | null): value is ModuleId =>
  !!value && MODULE_IDS.includes(value as ModuleId);

export const isSafeInternalPath = (path: string | null): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

const isPostAuthPublicPath = (path?: string): boolean => {
  if (typeof path !== 'string') {
    return false;
  }
  const pathname = normalizeShellPath(toPathname(path));
  return POST_AUTH_PUBLIC_PREFIXES.some((prefix) => isPathAtOrUnderPrefix(pathname, prefix));
};

export const isStaffPersona = (persona: string): boolean =>
  STAFF_PERSONAS.includes(canonicalizePersona(persona));

const isModuleAllowedForGroups = (
  moduleId: string,
  authGroups?: string[] | null,
): boolean => {
  if (moduleId === 'attendance' && authGroups) {
    return authGroups.some((group) => {
      const normalizedGroup = normalizePersonaToken(group);
      const canonicalGroup = parseCanonicalOrMigrationCognitoGroup(normalizedGroup);
      return ATTENDANCE_ALLOWED_GROUPS.has(canonicalGroup ?? normalizedGroup);
    });
  }
  if (!authGroups || !SHELL_REVIEW_GATED_MODULE_IDS.has(moduleId as ModuleId)) {
    return true;
  }
  return hasModuleReviewAccess(authGroups, moduleId);
};

export const resolveAllowedModules = (
  persona: string,
  enabledModuleIds?: Set<string> | string[] | null,
  authGroups?: string[] | null,
): ShellModule[] => {
  const enabledSet = enabledModuleIds
    ? enabledModuleIds instanceof Set
      ? enabledModuleIds
      : new Set(enabledModuleIds)
    : null;
  return SHELL_MODULES.filter((module) => (
    !module.retired
    && module.allowedPersonas.includes(canonicalizePersona(persona))
    && (!enabledSet || enabledSet.has(module.id))
    && isModuleAllowedForGroups(module.id, authGroups)
  ));
};

export const resolvePersonaDefaultEntry = (
  persona: string,
  preferredModule?: ModuleId | null,
  enabledModuleIds?: Set<string> | string[] | null,
  authGroups?: string[] | null,
  parentDefaultPath?: string | null,
): string => {
  const canonicalPersona = canonicalizePersona(persona);
  if (canonicalPersona === 'parent') {
    return normalizePath(parentDefaultPath || INTAKE_PARENT_DEFAULT_ROUTE);
  }

  const allowedModules = resolveAllowedModules(canonicalPersona, enabledModuleIds, authGroups);
  const resolveAllowedEntry = (module: ShellModule | undefined): string | null => {
    if (!module) return null;
    if (!module.allowedPersonas.includes(canonicalPersona)) return null;
    if (!isModuleAllowedForGroups(module.id, authGroups)) return null;
    return resolveEntryPath(module, canonicalPersona);
  };

  if (preferredModule) {
    const preferred = [...allowedModules, SETTINGS_MODULE].find((item) => item.id === preferredModule);
    const preferredEntry = resolveAllowedEntry(preferred);
    if (preferredEntry) return preferredEntry;
  }

  for (const module of allowedModules) {
    const entry = resolveAllowedEntry(module);
    if (entry) return entry;
  }

  const settingsEntry = resolveAllowedEntry(SETTINGS_MODULE);
  if (settingsEntry) return settingsEntry;
  return '/';
};

const normalizePathForPersona = (path?: string | null, persona?: string): string | null => {
  if (!path || !isSafeInternalPath(path)) {
    return null;
  }

  let pathname = path;
  let search = '';
  try {
    const parsed = new URL(path, 'https://triedge.local');
    pathname = parsed.pathname;
    search = parsed.search;
  } catch {
    const [rawPathname, rawSearch = ''] = path.split('?');
    pathname = rawPathname || path;
    search = rawSearch ? `?${rawSearch}` : '';
  }

  const normalizedPathname = normalizeShellPath(pathname);
  if (!isCanonicalRoutePath(normalizedPathname)) {
    return null;
  }

  if (persona && canonicalizePersona(persona) === 'parent' && isParentRoutePath(normalizedPathname)) {
    return search ? `${normalizedPathname}${search}` : normalizedPathname;
  }

  return search ? `${normalizePath(pathname)}${search}` : normalizePath(pathname);
};

const isPathAllowedForPersona = (
  path: string,
  persona: string,
  authGroups?: string[] | null,
): boolean => {
  const canonicalPersona = canonicalizePersona(persona);
  const pathname = normalizeShellPath(toPathname(path));

  if (!isCanonicalRoutePath(pathname)) {
    return false;
  }

  if (isPostAuthPublicPath(path)) {
    return true;
  }

  if (canonicalPersona === 'parent') {
    return isParentRoutePath(pathname);
  }

  if (isSuperAdministratorRoutePath(pathname)) {
    return canonicalPersona === 'super-administrator';
  }

  if (isAdministratorRoutePath(pathname)) {
    return canonicalPersona === 'administrator' || canonicalPersona === 'super-administrator';
  }

  if (!isStaffPersona(canonicalPersona)) {
    return false;
  }

  const module = resolveModuleFromPath(pathname);
  if (module?.retired) {
    return false;
  }
  if (module?.id === 'payroll' && isSelfGuardedSchoolRoutePath(pathname)) {
    return true;
  }
  return (
    module != null
    && module.allowedPersonas.includes(canonicalPersona as ShellPersona)
    && isModuleAllowedForGroups(module.id, authGroups)
  );
};

export const resolvePostAuthTarget = (options: {
  persona: string;
  lastVisitedPath?: string;
  defaultModule?: ModuleId | null;
  returnTo?: string | null;
  enabledModuleIds?: Set<string> | string[] | null;
  authGroups?: string[] | null;
  parentDefaultPath?: string | null;
}): string => {
  const normalizedReturnTo = normalizePathForPersona(options.returnTo, options.persona);
  if (normalizedReturnTo && isPathAllowedForPersona(normalizedReturnTo, options.persona, options.authGroups)) {
    return normalizedReturnTo;
  }

  const normalizedLastVisitedPath = normalizePathForPersona(options.lastVisitedPath, options.persona);
  if (normalizedLastVisitedPath && isPathAllowedForPersona(normalizedLastVisitedPath, options.persona, options.authGroups)) {
    return normalizedLastVisitedPath;
  }

  return resolvePersonaDefaultEntry(
    options.persona,
    options.defaultModule,
    options.enabledModuleIds,
    options.authGroups,
    options.parentDefaultPath,
  );
};

export const resolveShellRouteViolation = (
  pathname: string,
  persona: string,
  authGroups?: string[] | null,
): string | null => {
  const canonicalPersona = canonicalizePersona(persona);
  const normalized = normalizeShellPath(pathname);

  if (!isCanonicalRoutePath(normalized)) {
    return (
      `[YSTNJ routing] Non-canonical route "${normalized}" is blocked. `
      + 'Allowed routes are limited to owned school and parent portal surfaces.'
    );
  }

  if (isParentRoutePath(normalized) && canonicalPersona !== 'parent') {
    return `[YSTNJ routing] Persona "${persona}" cannot access parent route "${normalized}".`;
  }

  if (
    isSuperAdministratorRoutePath(normalized)
    && canonicalPersona !== 'super-administrator'
  ) {
    return (
      `[YSTNJ routing] Persona "${persona}" is not authorized for super-administrator route "${normalized}". `
      + 'This path requires super-administrator access.'
    );
  }

  if (isDiscontinuedSettingsPath(normalized)) {
    return (
      `[YSTNJ routing] Legacy settings integrations route "${normalized}" is discontinued. `
      + 'Use the super-administrator control-plane route family instead.'
    );
  }

  if (
    isAdministratorRoutePath(normalized)
    && canonicalPersona !== 'administrator'
    && canonicalPersona !== 'super-administrator'
  ) {
    return (
      `[YSTNJ routing] Persona "${persona}" is not authorized for administrator route "${normalized}". `
      + 'This path requires administrator or super-administrator access.'
    );
  }

  if (isSchoolRoutePath(normalized) && !isStaffPersona(canonicalPersona)) {
    return `[YSTNJ routing] Persona "${persona}" cannot access school route "${normalized}".`;
  }

  const restrictedModule = resolveModuleFromPath(normalized);
  if (restrictedModule?.retired) {
    return (
      `[YSTNJ routing] Module "${restrictedModule.id}" is retired and unavailable at "${normalized}". `
      + 'This path is no longer part of the active shell module set.'
    );
  }
  if (restrictedModule?.id === 'payroll' && isSelfGuardedSchoolRoutePath(normalized)) {
    return null;
  }
  if (
    restrictedModule
    && isReviewScopedModuleId(restrictedModule.id)
    && !isModuleAllowedForGroups(restrictedModule.id, authGroups)
  ) {
    return resolveReviewAccessDeniedMessage(restrictedModule.id, {
      pathname: normalized,
    });
  }
  if (restrictedModule && !isModuleAllowedForGroups(restrictedModule.id, authGroups)) {
    return (
      `[YSTNJ routing] Persona "${persona}" is not authorized for module "${restrictedModule.id}" at "${normalized}". `
      + 'This path requires a staff, administrator, owner, or super-administrator group.'
    );
  }
  if (restrictedModule && !restrictedModule.allowedPersonas.includes(canonicalPersona as ShellPersona)) {
    return (
      `[YSTNJ routing] Persona "${persona}" is not authorized for module "${restrictedModule.id}" at "${normalized}". `
      + 'This path requires administrator or super-administrator access.'
    );
  }

  return null;
};
